/**
 * Technique Image Manager
 * 
 * Handles lazy loading and caching of technique images for offline functionality.
 * Ensures all technique images are available when navigating between pages in offline mode.
 */

import { indexedDBService } from '@/services/offline/indexeddb';
import { logoAssetManager } from './LogoAssetManager';

export class TechniqueImageManager {
  private isInitialized = false;
  private cachingInProgress = false;
  private cachedImageUrls = new Set<string>();

  /**
   * Initialize the technique image manager
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      await indexedDBService.initialize();
      this.isInitialized = true;
    } catch (error) {
      console.error('❌ Failed to initialize TechniqueImageManager:', error);
      throw error;
    }
  }

  /**
   * Start lazy loading of all technique images after initial data load
   */
  async startLazyImageCaching(): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    if (this.cachingInProgress) {

      return;
    }

    if (!navigator.onLine) {
      console.log('📱 Offline, skipping technique image caching');
      return;
    }

    try {
      this.cachingInProgress = true;


      // Get all techniques from cache
      const techniques = await indexedDBService.techniques.getAll();
      
      // Extract unique image URLs
      const imageUrls = techniques
        .map(technique => technique.image_url)
        .filter((url): url is string => Boolean(url))
        .filter(url => !this.cachedImageUrls.has(url));

      if (imageUrls.length === 0) {
        console.log('✅ No new technique images to cache');
        return;
      }



      // Cache images using the logo asset manager
      await logoAssetManager.cacheTechniqueImages(imageUrls);

      // Mark URLs as cached
      imageUrls.forEach(url => this.cachedImageUrls.add(url));


    } catch (error) {
      console.error('❌ Failed to cache technique images:', error);
    } finally {
      this.cachingInProgress = false;
    }
  }

  /**
   * Get cached image URL for a technique image
   */
  async getCachedImageUrl(originalUrl: string): Promise<string | null> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      return await logoAssetManager.getCachedLogoUrl(originalUrl);
    } catch (error) {
      console.error(`❌ Failed to get cached image for ${originalUrl}:`, error);
      return null;
    }
  }

  /**
   * Check if an image is cached
   */
  async isImageCached(url: string): Promise<boolean> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      return await logoAssetManager.isLogoCached(url);
    } catch (error) {
      console.error(`❌ Failed to check cache status for ${url}:`, error);
      return false;
    }
  }

  /**
   * Clear expired technique images from cache
   */
  async clearExpiredImages(): Promise<number> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      return await logoAssetManager.clearExpiredLogos();
    } catch (error) {
      console.error('❌ Failed to clear expired technique images:', error);
      return 0;
    }
  }

  /**
   * Get caching status
   */
  getStatus(): {
    isInitialized: boolean;
    cachingInProgress: boolean;
    cachedImageCount: number;
  } {
    return {
      isInitialized: this.isInitialized,
      cachingInProgress: this.cachingInProgress,
      cachedImageCount: this.cachedImageUrls.size
    };
  }
}

// Export singleton instance
export const techniqueImageManager = new TechniqueImageManager();
